import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { updateItem } from 'Vendor/Store/studio';
import { Font, FontVariant, GridItemStyle } from 'Vendor/Utils/StudioTypes';
import { AppDispatch, RootState } from 'Vendor/Store/store';
import { selectSingleSelectedItem } from 'Vendor/Store/studio/selectors';
import { ActiveLayoutItem } from 'Vendor/Store/studio/initialState';
import FontFamilyDropdown from 'Components/Studio/SideMenu/TextBlock/FontFamilyDropdown';
import { FONT_WEIGHT } from 'Vendor/Utils/Constants';
import { normalizeFontWeight } from 'Vendor/Utils/Fonts';

interface Properties {
    platformFonts: Font[];
};

const EditText: React.FC<Properties> = ({ platformFonts }) => {
    const dispatch = useDispatch<AppDispatch>();
    const selectedItem = useSelector(selectSingleSelectedItem)
    const [localItem, setLocalItem] = useState<ActiveLayoutItem | null>(null);
    const activePageId = useSelector((state: RootState) => state.studio.present.activePageId);

    useEffect(() => {
        setLocalItem(selectedItem ? { ...selectedItem } : null);
    }, [selectedItem]);

    const handleContentChange = useCallback((field: string, value: string) => {
        setLocalItem(prev => {
            if (!prev) return prev;
            const updatedItem = { ...prev, content: { ...prev.content, [field]: value } };
            if (activePageId) dispatch(updateItem({ pageId: activePageId, updatedItem }));
            return updatedItem;
        });
    }, [activePageId, dispatch]);

    const handleStyleChange = useCallback((style: keyof GridItemStyle, value: string | undefined) => {
        setLocalItem(prev => {
            if (!prev) return prev;
            const updatedItem = { ...prev, styles: { ...prev.styles, [style]: value } };
            if (activePageId) dispatch(updateItem({ pageId: activePageId, updatedItem }));
            return updatedItem;
        });
    }, [activePageId, dispatch]);

    const handleFontVariantChange = useCallback((family: string, variant: FontVariant) => {
        setLocalItem(prev => {
            if (!prev) return prev;
            const updatedItem = {
                ...prev,
                styles: {
                    ...prev.styles,
                    fontFamily: family,
                    fontWeight: variant.weight?.toString() || String(FONT_WEIGHT.REGULAR),
                    fontStyle: variant.style || 'normal',
                },
            };
            if (activePageId) dispatch(updateItem({ pageId: activePageId, updatedItem }));
            return updatedItem;
        });
    }, [activePageId, dispatch]);

    const selectedVariantName = useMemo(() => {
        if (!localItem) return undefined;

        const font = platformFonts.find(f => f.family === localItem.styles.fontFamily);

        if (!font) return undefined;

        const currentWeight = normalizeFontWeight(localItem.styles.fontWeight);
        const currentStyle = localItem.styles.fontStyle || 'normal';
        const variant = font.variants.find(v => {
            const vWeight = v.weight?.toString() || String(FONT_WEIGHT.REGULAR);
            const vStyle = v.style || 'normal';

            return vWeight === String(currentWeight) && vStyle === String(currentStyle);
        });

        return variant?.name
    }, [localItem, platformFonts]);

    const toggleTextDecoration = (decoration: 'underline' | 'line-through') => {
        if (!localItem) return;

        const currentDecorations = String(localItem.styles.textDecoration || '');
        const decorations = currentDecorations.split(' ').filter((d: string) => d.length > 0);

        const hasDecoration = decorations.includes(decoration);

        let newDecorations;

        if (hasDecoration) {
            newDecorations = decorations.filter((d: string) => d !== decoration);
        } else {
            newDecorations = [...decorations, decoration];
        }

        const textDecorationValue = newDecorations.length > 0 ? newDecorations.join(' ') : undefined;
        handleStyleChange('textDecoration', textDecorationValue);
    };

    const toggleStyle = (style: keyof GridItemStyle, activeValue: string, inactiveValue: string = '') => {
        const current = localItem?.styles[style] || '';
        handleStyleChange(style, current === activeValue ? inactiveValue : activeValue);
    };

    const isActive = (style: keyof GridItemStyle, value: string) => localItem?.styles[style] === value;

    const isTextDecorationActive = (decoration: string) => {
        const currentDecorations = String(localItem?.styles.textDecoration || '');
        return currentDecorations.includes(decoration);
    };

    if (!selectedItem || !localItem || selectedItem.type !== 'TEXT') {
        return null; // Or a placeholder for when a non-text item is selected
    }

    return (
        <>
            <div className="my-3">
                <h3 className="h5 fw-medium font-headline mb-1 text-white">Properties</h3>
                <p className="small text-muted text-white mb-0">Editing: {localItem.type.toLowerCase()} item</p>
            </div>

            <div className="mb-3">
                <label htmlFor="text-content" className="form-label fs-5 text-white">Text</label>
                <textarea
                    id="text-content"
                    className="form-control py-2"
                    value={localItem.content.text || ''}
                    onChange={(e) => handleContentChange('text', e.target.value)}
                />
            </div>

            <div className="mb-3">
                <label className="form-label fs-5 text-white">Typography</label>
                <div className="d-flex align-items-center gap-2">
                    <FontFamilyDropdown
                        key={localItem.id}
                        value={localItem.styles.fontFamily || ''}
                        onChange={(value) => handleStyleChange('fontFamily', value)}
                        onVariantChange={handleFontVariantChange}
                        platformFonts={platformFonts}
                        showVariants
                        selectedVariantName={selectedVariantName}
                    />
                </div>
            </div>

            <div className="mb-3 w-100">
                <label htmlFor="text-color" className="form-label fs-5 text-white">Text Color</label>
                <input
                    id="text-color"
                    type="color"
                    className="form-control form-control-color p-1 w-100"
                    style={{ height: '40px' }}
                    value={String(localItem.styles.color || '#000000')}
                    onChange={(e) => handleStyleChange('color', e.target.value)}
                />
            </div>

            <div className="mb-3">
                <label className="form-label fs-5 text-white">Font Size</label>
                <div className="d-flex align-items-center gap-2">
                    <input
                        type="range"
                        className="form-range"
                        min="8"
                        max="72"
                        value={parseInt(String(localItem.styles.fontSize || '16'))}
                        onChange={(e) => handleStyleChange('fontSize', `${e.target.value}px`)}
                    />
                    <span className="text-white">{localItem.styles.fontSize || '16px'}</span>
                </div>
            </div>

            <div className="mb-3">
                <label className="form-label fs-5 text-white">Style</label>
                <div className="d-flex gap-3 align-items-center px-2">
                    <i
                        className={`fa-solid fa-bold fs-3 cursor-pointer ${isActive('fontWeight', String(FONT_WEIGHT.BOLD)) ? 'text-warning' : 'text-secondary'}`}
                        onClick={() => toggleStyle('fontWeight', String(FONT_WEIGHT.BOLD), String(FONT_WEIGHT.REGULAR))}
                        title="Bold"
                    />
                    <i
                        className={`fa-solid fa-underline fs-3 cursor-pointer ${isTextDecorationActive('underline') ? 'text-warning' : 'text-secondary'}`}
                        onClick={() => toggleTextDecoration('underline')}
                        title="Underline"
                    />
                    <i
                        className={`fa-solid fa-italic fs-3 cursor-pointer ${isActive('fontStyle', 'italic') ? 'text-warning' : 'text-secondary'}`}
                        onClick={() => toggleStyle('fontStyle', 'italic')}
                        title="Italic"
                    />
                    <i
                        className={`fa-solid fa-strikethrough fs-3 cursor-pointer ${isTextDecorationActive('line-through') ? 'text-warning' : 'text-secondary'}`}
                        onClick={() => toggleTextDecoration('line-through')}
                        title="Strikethrough"
                    />
                </div>
            </div>

            <div className="mb-3">
                <label className="form-label fs-5 text-white">Paragraph</label>
                <div className="d-flex gap-3">
                    <i
                        className={`fa-solid fa-align-left fs-5 cursor-pointer ${isActive('textAlign', 'left') ? 'text-warning' : 'text-secondary'}`}
                        onClick={() => handleStyleChange('textAlign', 'left')}
                        title="Align Left"
                    />
                    <i
                        className={`fa-solid fa-align-center fs-5 cursor-pointer ${isActive('textAlign', 'center') ? 'text-warning' : 'text-secondary'}`}
                        onClick={() => handleStyleChange('textAlign', 'center')}
                        title="Align Center"
                    />
                    <i
                        className={`fa-solid fa-align-right fs-5 cursor-pointer ${isActive('textAlign', 'right') ? 'text-warning' : 'text-secondary'}`}
                        onClick={() => handleStyleChange('textAlign', 'right')}
                        title="Align Right"
                    />
                    <i
                        className={`fa-solid fa-align-justify fs-5 cursor-pointer ${isActive('textAlign', 'justify') ? 'text-warning' : 'text-secondary'}`}
                        onClick={() => handleStyleChange('textAlign', 'justify')}
                        title="Justify"
                    />
                </div>
            </div>
        </>
    );
};

export default EditText;
